import { type SupportZendeskTicketModel } from "@doitintl/cmp-models";
import { type ChipProps } from "@mui/material";
import { type TFunction } from "i18next";

import { defaultHeaderStyle } from "../../../Components/FilterTable/Table/FilterTableHeader";
import { Comparators, type DataColumn, type HeaderColumn } from "../../../types/FilterTable";
import { transformPriority, transformStatus } from "../../../utils/common";

export const defaultToOption = (value: string | number | null | undefined) => {
  if (!value) return { value: "", label: "" };
  return {
    value,
    label: value.toString(),
  };
};

export const getStatusColor = (status): ChipProps["color"] => {
  switch (status) {
    case "new":
      return "info";
    case "open":
      return "primary";
    case "pending":
      return "info";
    case "hold":
      return "primary";
    case "solved":
      return "success";
    case "closed":
      return "default";
    default:
      return "default";
  }
};

export const zendeskTicketColumns = (t: TFunction<"services">) => [
  {
    path: "ticket.status",
    label: "Status",
    comparators: ["==", "!="],
    toOption: (value) => {
      if (!value) return { value: "", label: "" };
      return {
        value,
        label: transformStatus(value, t) || value.toString(),
      };
    },
  },
  {
    path: "ticket.id",
    label: "Ticket ID",
    toOption: defaultToOption,
  },
  {
    path: "ticket.requester_email",
    label: "Requester",
    toOption: defaultToOption,
  },
  {
    path: "ticket.subject",
    label: "Subject",
    comparators: ["contains"],
    toOption: defaultToOption,
  },
  {
    path: "ticket.priority",
    label: "Severity",
    toOption: (value) => {
      if (!value) return { value: "", label: "" };
      return {
        value,
        label: transformPriority(value, t) || value.toString(),
      };
    },
  },
];

export const supportTicketsColumnsData: (t: TFunction<"services">) => DataColumn[] = (t) => [
  {
    path: "zendeskId",
    label: "Ticket ID",
    toOption: defaultToOption,
    compareFn: (a, b) => parseInt(a, 10) - parseInt(b, 10),
  },
  {
    path: "ticket.subject",
    label: t("TicketsTableColumns.subject"),
    comparators: ["contains"],
    toOption: defaultToOption,
  },
  {
    path: "ticket.priority",
    label: t("TicketsTableColumns.severity"),
    toOption: (value) => {
      if (!value) return { value: "", label: "" };
      return {
        value,
        label: transformPriority(value, t) || value.toString(),
      };
    },
  },
  {
    path: "ticket.requester_email",
    label: t("TicketsTableColumns.requester"),
    toOption: defaultToOption,
  },
  {
    path: "ticket.satisfaction_rating.rating",
    label: t("TicketsTableColumns.satisfaction"),
    toOption: defaultToOption,
  },
  {
    path: "ticket.updated_at",
    label: t("TicketsTableColumns.lastModified"),
    type: "DateTime",
  },
  {
    path: "ticket.status",
    label: t("TicketsTableColumns.status"),
    comparators: [Comparators.EQUAL_TO, Comparators.NOT_EQUAL_TO],
    toOption: (value) => {
      if (!value) return { value: "", label: "" };
      return {
        value,
        label: transformStatus(value, t) || value.toString(),
      };
    },
  },
];

export const supportTicketsColumnsHeaders: (t: TFunction<"services">) => HeaderColumn[] = (t) => {
  const columnsData = supportTicketsColumnsData(t);
  return [
    {
      value: "zendeskIdNumeric",
      label: t("TicketsTableColumns.id"),
      style: defaultHeaderStyle,
      renderValue: (row: SupportZendeskTicketModel & { zendeskIdNumeric: number }): string => row.zendeskId,
    },
    {
      value: columnsData[1].path,
      label: columnsData[1].label,
      style: defaultHeaderStyle,
      hidden: { smDown: false },
    },
    {
      label: t("TicketsTableColumns.platform"),
      value: "@",
      hidden: { smDown: true },
    },
    {
      value: "ticket.assignee.email",
      label: t("TicketsTableColumns.specialist"),
      style: defaultHeaderStyle,
      hidden: { mdDown: true },
    },
    {
      value: columnsData[2].path,
      label: columnsData[2].label,
      style: defaultHeaderStyle,
      hidden: { smDown: true },
    },
    {
      value: columnsData[3].path,
      label: columnsData[3].label,
      style: defaultHeaderStyle,
      hidden: { mdDown: true },
    },
    {
      value: columnsData[4].path,
      label: columnsData[4].label,
      style: defaultHeaderStyle,
      hidden: { smDown: true },
    },
    {
      value: columnsData[5].path,
      label: columnsData[5].label,
      style: defaultHeaderStyle,
      hidden: { smDown: true },
    },
    {
      value: columnsData[6].path,
      label: columnsData[6].label,
      style: defaultHeaderStyle,
      hidden: { xsDown: true },
    },
  ];
};
